import React, { useState, useEffect, useContext } from "react";
import "./styles.scss";
import Lottie from "react-lottie";
import "react-datepicker/dist/react-datepicker.css";
import DataTable from "react-data-table-component";
import ModalCrud from "../../../Components/ModalCrud";
import MainContext from "../../../Contexts/MainContext";
import { useLocation } from "react-router-dom";

// Setores
import { getAllSetores } from "../../../Services/ApiCalls";
import { ColumsTableSetores } from "../../../Services/TableColumns";
import AddSelectedSetor from "../../../Components/RenderSelectedRow/Setores/AddSelectedSetor";
import SelectedSetor from "../../../Components/RenderSelectedRow/Setores/SelectedSetor";

// Funções
import { getAllFuncoes } from "../../../Services/ApiCalls";
import { ColumsTableFuncoes } from "../../../Services/TableColumns";
import AddSelectedFuncao from "../../../Components/RenderSelectedRow/Funcoes/AddSelectedFuncao";
import SelectedFuncao from "../../../Components/RenderSelectedRow/Funcoes/SelectedFuncao";

// Expediente (Horários / Jornada)
import { GetAllHorarios } from "../../../Services/ApiCalls";
import { ColumsTableHorarios } from "../../../Services/TableColumns";
import AddSelectedHorario from "../../../Components/RenderSelectedRow/Horarios/AddSelectedHorario";
import SelectedHorario from "../../../Components/RenderSelectedRow/Horarios/SelectedHorario";

// Feriados
import { GetAllFeriados, deleteFeriadoById } from "../../../Services/ApiCalls";
import AddSelectedFeriado from "../../../Components/RenderSelectedRow/Feriados/AddSelectedFeriado";
import SelectedFeriado from "../../../Components/RenderSelectedRow/Feriados/SelectedFeriado";
import { FaPen, FaTrash } from "react-icons/fa";
import { showToast } from "../../../Functions";

import EmptyData from "../../../Components/EmptyData";
import { AiFillCheckCircle } from "react-icons/ai";
import { FaTimesCircle } from "react-icons/fa";
import { IoMdBusiness } from "react-icons/io";

const LOADING = require("../../../Assets/animations/loading.json");

type TabType = "setores" | "funcoes" | "expediente" | "feriados";

const TAB_CONFIG: { key: TabType; label: string; icon: string }[] = [
    { key: "setores", label: "Setores", icon: "🏢" },
    { key: "funcoes", label: "Funções", icon: "⚙️" },
    { key: "expediente", label: "Expediente", icon: "📋" },
    { key: "feriados", label: "Feriados", icon: "📅" },
];

const EstruturaOrganizacional: React.FC = () => {
    const {
        token,
        openMoreInfo,
        setOpenMoreInfo,
        hasCloseEditModal,
        addModalOpen,
        setaddModalOpen,
    } = useContext(MainContext);

    const location = useLocation();

    const getInitialTab = (): TabType => {
        const params = new URLSearchParams(location.search);
        const tab = params.get("tab") as TabType;
        if (tab === "funcoes" || tab === "setores" || tab === "expediente" || tab === "feriados") return tab;
        return "setores";
    };

    const [activeTab, setActiveTab] = useState<TabType>(getInitialTab());

    const [selectedSetor, setSelectedSetor] = useState({});
    const [selectedFuncao, setSelectedFuncao] = useState({});
    const [selectedExpediente, setSelectedExpediente] = useState({});
    const [selectedFeriado, setSelectedFeriado] = useState({});

    // Dados manuais (Setores, Funções)
    const [isFetching, setIsFetching] = useState(false);
    const [dataSetores, setDataSetores] = useState<any[]>([]);
    const [dataFuncoes, setDataFuncoes] = useState<any[]>([]);

    // Hooks customizados (Horarios e Feriados)
    const [localRefreshFeriados, setLocalRefreshFeriados] = useState(false);
    const { dataAllHorarios, statusCodeAllHorarios } = GetAllHorarios(token, hasCloseEditModal);
    const { dataAllFeriados, statusCodeAllFeriados } = GetAllFeriados(token, hasCloseEditModal || localRefreshFeriados);

    useEffect(() => {
        let isMounted = true;
        const fetchData = async () => {
            if (!token) return;
            setIsFetching(true);
            try {
                if (activeTab === "setores") {
                    const res = await getAllSetores(token || "");
                    if (res && res.status === 200 && isMounted) {
                        const unique = res.data.filter((v: any, i: number, a: any[]) => a.findIndex(t => t.id === v.id) === i);
                        setDataSetores(unique);
                    }
                } else if (activeTab === "funcoes") {
                    const res = await getAllFuncoes(token || "");
                    if (res && res.status === 200 && isMounted) {
                        const unique = res.data.filter((v: any, i: number, a: any[]) => a.findIndex(t => t.id === v.id) === i);
                        setDataFuncoes(unique);
                    }
                }
            } finally {
                if (isMounted) setIsFetching(false);
            }
        };
        fetchData();
        return () => { isMounted = false; };
    }, [activeTab, token, hasCloseEditModal]);

    useEffect(() => {
        document.title = "Marca Ponto - Config.";
        setOpenMoreInfo(false);
    }, []);

    useEffect(() => {
        setOpenMoreInfo(false);
        setaddModalOpen(false);
    }, [activeTab]);

    const closeModal = () => { setaddModalOpen(false); return true; };
    const closeModalMoreInfo = () => { setOpenMoreInfo(false); return true; };

    const counts: Record<TabType, number> = {
        setores: dataSetores?.length || 0,
        funcoes: dataFuncoes?.length || 0,
        expediente: dataAllHorarios?.length || 0,
        feriados: dataAllFeriados?.length || 0,
    };

    const addLabels: Record<TabType, string> = {
        setores: "+ Novo Setor",
        funcoes: "+ Nova Função",
        expediente: "+ Novo Expediente",
        feriados: "+ Novo Feriado",
    };

    // ── Render Tables ──
    const renderSetoresTable = () => {
        if (isFetching) return renderLoading();
        return (
            <DataTable
                noHeader={true}
                data={dataSetores.map((c: any) =>
                    c.ativo
                        ? { ...c, ativo: <AiFillCheckCircle color="green" size={20} /> }
                        : { ...c, ativo: <FaTimesCircle color="red" size={20} /> }
                )}
                columns={ColumsTableSetores}
                striped={true}
                pagination={true}
                onRowClicked={(row: any) => { setOpenMoreInfo(true); setSelectedSetor(row); }}
                pointerOnHover={true}
                highlightOnHover={true}
            />
        );
    };

    const renderFuncoesTable = () => {
        if (isFetching) return renderLoading();
        return (
            <DataTable
                noHeader={true}
                data={dataFuncoes.map((c: any) =>
                    c.responsavel
                        ? { ...c, responsavel: <AiFillCheckCircle color="green" size={20} /> }
                        : { ...c, responsavel: <FaTimesCircle color="red" size={20} /> }
                )}
                columns={ColumsTableFuncoes}
                striped={true}
                pagination={true}
                onRowClicked={(row: any) => { setOpenMoreInfo(true); setSelectedFuncao(row); }}
                pointerOnHover={true}
                highlightOnHover={true}
            />
        );
    };

    const renderExpedienteTable = () => {
        if (statusCodeAllHorarios !== 200) return renderLoading();
        return (
            <DataTable
                noHeader={true}
                data={dataAllHorarios.map((c: any) => ({
                    ...c,
                    padraoFormatado: c.padrao ? "Sim" : "Não"
                }))}
                columns={ColumsTableHorarios}
                noDataComponent={<EmptyData hasMargin={true} />}
                striped={true}
                pagination={true}
                onRowClicked={(row: any) => { setOpenMoreInfo(true); setSelectedExpediente(row); }}
                pointerOnHover={true}
                highlightOnHover={true}
                paginationPerPage={30}
            />
        );
    };

    const handleDeleteFeriado = async (row: any) => {
        if (window.confirm("Tem certeza que deseja excluir o feriado " + row.nome + "?")) {
            try {
                await deleteFeriadoById(token, row.id);
                showToast("SUCCESS", "Feriado excluído com sucesso", {});
                setLocalRefreshFeriados(!localRefreshFeriados);
            } catch (error) {
                showToast("ERROR", "Erro ao excluir feriado", {});
            }
        }
    };

    const feriadosColumns = [
        {
            name: "Nome do Feriado",
            selector: (row: any) => row.nome,
            sortable: true,
            cell: (row: any) => <span className="fw-600">{row.nome}</span>
        },
        {
            name: "Data",
            selector: (row: any) => row.data,
            sortable: true,
            cell: (row: any) => {
                const dateParts = row.data.split("-");
                if (dateParts.length === 3) {
                    return <span>{`${dateParts[2]}/${dateParts[1]}/${dateParts[0]}`}</span>;
                }
                return <span>{row.data}</span>;
            }
        },
        {
            name: "Tipo",
            selector: (row: any) => row.tipo,
            sortable: true,
            cell: (row: any) => (
                <span className={`badge-light ${row.tipo === "Nacional" ? "nacional" : "municipal"}`}>
                    {row.tipo}
                </span>
            )
        },
        {
            name: "Alíquota (%)",
            selector: (row: any) => row.aliquota,
            sortable: true,
            center: true,
            cell: (row: any) => <span className="text-purple">{row.aliquota}%</span>
        },
        {
            name: "Ações",
            center: true,
            width: "120px",
            cell: (row: any) => (
                <div className="row-actions">
                    <button className="btn-action edit" onClick={(e: any) => { e.stopPropagation(); setOpenMoreInfo(true); setSelectedFeriado(row); }} title="Editar">
                        <FaPen size={14} />
                    </button>
                    <button className="btn-action delete" onClick={(e: any) => { e.stopPropagation(); handleDeleteFeriado(row); }} title="Excluir">
                        <FaTrash size={14} />
                    </button>
                </div>
            )
        }
    ];

    const renderFeriadosTable = () => {
        if (statusCodeAllFeriados !== 200) return renderLoading();
        return (
            <DataTable
                noHeader={true}
                data={dataAllFeriados || []}
                columns={feriadosColumns as any}
                noDataComponent={<EmptyData hasMargin={true} />}
                striped={false}
                pagination={true}
                onRowClicked={(row: any) => { setOpenMoreInfo(true); setSelectedFeriado(row); }}
                pointerOnHover={true}
                highlightOnHover={true}
                customStyles={{
                    rows: { style: { minHeight: '60px' } }
                }}
            />
        );
    };

    const renderLoading = () => (
        <div className="eo-loading">
            <Lottie options={{ loop: true, animationData: LOADING }} height={120} width={120} />
            <p>Carregando dados…</p>
        </div>
    );

    const renderActiveTable = () => {
        switch (activeTab) {
            case "setores": return renderSetoresTable();
            case "funcoes": return renderFuncoesTable();
            case "expediente": return renderExpedienteTable();
            case "feriados": return renderFeriadosTable();
        }
    };

    const renderAddModal = () => {
        switch (activeTab) {
            case "setores": return <AddSelectedSetor />;
            case "funcoes": return <AddSelectedFuncao />;
            case "expediente": return <AddSelectedHorario />;
            case "feriados": return <AddSelectedFeriado />;
        }
    };

    const renderEditModal = () => {
        switch (activeTab) {
            case "setores": return selectedSetor ? <SelectedSetor data={selectedSetor} /> : "";
            case "funcoes": return selectedFuncao ? <SelectedFuncao data={selectedFuncao} /> : "";
            case "expediente": return selectedExpediente ? <SelectedHorario data={selectedExpediente} /> : "";
            case "feriados": return selectedFeriado ? <SelectedFeriado data={selectedFeriado} /> : "";
        }
    };

    return (
        <>
            <div className="estrutura-org__wrapper">
                {/* Header compacto */}
                <div className="eo-header">
                    <div className="eo-header__title">
                        <IoMdBusiness size={24} color="#5850EC" />
                        <h2>Configurações</h2>
                    </div>
                    <button className="eo-header__add-btn" onClick={() => setaddModalOpen(true)}>
                        {addLabels[activeTab]}
                    </button>
                </div>

                {/* Tabs */}
                <div className="eo-tabs">
                    {TAB_CONFIG.map(tab => (
                        <button
                            key={tab.key}
                            className={`eo-tab ${activeTab === tab.key ? "eo-tab--active" : ""} ${isFetching ? "eo-tab--disabled" : ""}`}
                            disabled={isFetching}
                            onClick={() => !isFetching && setActiveTab(tab.key)}
                        >
                            <span className="eo-tab__icon">{tab.icon}</span>
                            <span className="eo-tab__label">{tab.label}</span>
                            <span className="eo-tab__count">{counts[tab.key]}</span>
                        </button>
                    ))}
                </div>

                {/* Tabela */}
                <div className="eo-table-container">
                    {renderActiveTable()}
                </div>
            </div>

            {/* Modal Novo */}
            {addModalOpen && (
                <ModalCrud onClose={closeModal}>
                    {renderAddModal()}
                </ModalCrud>
            )}

            {/* Modal Edição */}
            {openMoreInfo && (
                <ModalCrud onClose={closeModalMoreInfo}>
                    {renderEditModal()}
                </ModalCrud>
            )}
        </>
    );
};

export default EstruturaOrganizacional;
